import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./OfferCountdownTimer.css";

/**
 * OfferCountdownTimer Component
 * Displays a countdown timer for product offers
 * 
 * @param {string} offerValidUntil - ISO 8601 date string for when the offer expires
 * @param {boolean} compact - If true, shows a more compact version of the timer
 */
const OfferCountdownTimer = ({ offerValidUntil, compact = false }) => {
  const [_] = useTranslation("global");
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [isExpired, setIsExpired] = useState(false);

  function calculateTimeLeft() {
    if (!offerValidUntil) return null;

    const now = new Date();
    const endDate = new Date(offerValidUntil);
    const difference = endDate - now;

    if (difference <= 0) {
      return null;
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return { days, hours, minutes, seconds };
  }

  useEffect(() => {
    if (!offerValidUntil) {
      setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      if (!newTimeLeft) {
        setIsExpired(true);
        clearInterval(timer);
      } else {
        setTimeLeft(newTimeLeft);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [offerValidUntil]);

  if (isExpired || !timeLeft) {
    return null;
  }

  const formatNumber = (num) => String(num).padStart(2, "0");

  if (compact) {
    return (
      <div className="offer-countdown-timer compact">
        <span className="timer-icon">⏱</span>
        <span className="timer-text">
          {timeLeft.days > 0 && (
            <span className="timer-days">{timeLeft.days}{_("home.offerDays")}</span>
          )}
          <span className="timer-time">{formatNumber(timeLeft.hours)}:{formatNumber(timeLeft.minutes)}:{formatNumber(timeLeft.seconds)}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="offer-countdown-timer">
      <div className="timer-label">{_("home.offerEndsIn")}</div>
      <div className="timer-units">
        {timeLeft.days > 0 && (
          <div className="timer-unit">
            <span className="timer-value">{formatNumber(timeLeft.days)}</span>
            <span className="timer-unit-label">{_("home.offerDays")}</span>
          </div>
        )}
        <div className="timer-unit">
          <span className="timer-value">{formatNumber(timeLeft.hours)}</span>
          <span className="timer-unit-label">{_("home.offerHours")}</span>
        </div>
        <div className="timer-unit">
          <span className="timer-value">{formatNumber(timeLeft.minutes)}</span>
          <span className="timer-unit-label">{_("home.offerMinutes")}</span>
        </div>
        <div className="timer-unit">
          <span className="timer-value">{formatNumber(timeLeft.seconds)}</span>
          <span className="timer-unit-label">{_("home.offerSeconds")}</span>
        </div>
      </div>
    </div>
  );
};

export default OfferCountdownTimer;
